import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, AlertTriangle, Smartphone, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Html5Qrcode } from 'html5-qrcode';

export default function QRScannerModal({ isOpen, onClose, onScan }) {
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraList, setCameraList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Cleanup scanner when modal closes or component unmounts
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopScanner();
      setShowScanner(false);
      setError('');
    }
  }, [isOpen]);

  const stopScanner = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
        setIsScanning(false);
      } catch (err) {
        console.log('Scanner already stopped');
      }
    }
  };

  // Lister les caméras disponibles
  const listCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      setCameraList(devices);
      // Préférer la caméra arrière
      const backCamera = devices.find(d => 
        d.label.toLowerCase().includes('back') || 
        d.label.toLowerCase().includes('arrière') ||
        d.label.toLowerCase().includes('rear') ||
        d.label.toLowerCase().includes('environment')
      );
      setSelectedCamera(backCamera || devices[0]);
      return devices;
    } catch (err) {
      console.error('Error listing cameras:', err);
      return [];
    }
  };

  const startScanner = async () => {
    setError('');
    setShowScanner(true);

    // Wait for DOM element to be ready
    setTimeout(async () => {
      try {
        if (!scannerRef.current) return;

        // Lister les caméras si pas encore fait
        let cameras = cameraList;
        if (cameras.length === 0) {
          cameras = await listCameras();
        }

        html5QrCodeRef.current = new Html5Qrcode('qr-reader');
        
        // Configuration adaptée aux appareils bas de gamme (Samsung A11, etc.)
        const config = {
          fps: 5, // Réduire les FPS pour les appareils lents
          qrbox: function(viewfinderWidth, viewfinderHeight) {
            // QR box responsive - plus petit pour les petits écrans
            const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            const qrboxSize = Math.floor(minEdge * 0.7); // 70% de la plus petite dimension
            return { width: qrboxSize, height: qrboxSize };
          },
          aspectRatio: 1.0,
          disableFlip: false, // Permettre le flip horizontal
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true // Utiliser l'API native si disponible
          }
        };

        // Utiliser la caméra sélectionnée ou l'environnement par défaut
        const cameraId = selectedCamera?.id 
          ? { deviceId: selectedCamera.id }
          : { facingMode: 'environment' };

        await html5QrCodeRef.current.start(
          cameraId,
          config,
          (decodedText) => {
            // QR Code detected
            handleSuccessfulScan(decodedText);
          },
          (errorMessage) => {
            // Ignore scan errors (no QR found yet)
          }
        );
        
        setIsScanning(true);
      } catch (err) {
        console.error('Scanner error:', err);
        setError(getErrorMessage(err));
        setShowScanner(false);
      }
    }, 200); // Augmenter le délai pour les appareils lents
  };

  // Changer de caméra
  const switchCamera = async () => {
    if (cameraList.length < 2) return;
    
    const currentIndex = cameraList.findIndex(c => c.id === selectedCamera?.id);
    const nextIndex = (currentIndex + 1) % cameraList.length;
    const nextCamera = cameraList[nextIndex];
    
    await stopScanner();
    setSelectedCamera(nextCamera);
    
    // Redémarrer avec la nouvelle caméra
    setTimeout(() => startScanner(), 300);
  };

  const getErrorMessage = (err) => {
    const errorStr = String(err);
    if (errorStr.includes('NotAllowedError') || errorStr.includes('Permission')) {
      return "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.";
    }
    if (errorStr.includes('NotFoundError') || errorStr.includes('No camera')) {
      return "Aucune caméra détectée sur cet appareil.";
    }
    if (errorStr.includes('NotReadableError') || errorStr.includes('in use')) {
      return "La caméra est utilisée par une autre application. Fermez les autres apps et réessayez.";
    }
    if (errorStr.includes('OverconstrainedError')) {
      return "Configuration caméra non supportée. Essayez avec une autre caméra.";
    }
    return "Erreur d'accès à la caméra. Utilisez la saisie manuelle du code.";
  };

  const handleSuccessfulScan = async (code) => {
    // Nettoyer le code scanné (espaces, retours à la ligne, etc.)
    const cleanCode = code.trim().toUpperCase();
    console.log('📱 QR Scanné:', code, '-> Nettoyé:', cleanCode);
    
    await stopScanner();
    onScan(cleanCode);
    onClose();
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      // Nettoyer le code manuel aussi
      const cleanCode = manualCode.trim().toUpperCase();
      console.log('⌨️ Code manuel:', manualCode, '-> Nettoyé:', cleanCode);
      onScan(cleanCode);
      onClose();
      setManualCode('');
    }
  };

  const handleClose = async () => {
    await stopScanner();
    setManualCode('');
    setError('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            data-testid="qr-scanner-backdrop"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div 
              className="backdrop-blur-xl rounded-3xl p-6 pointer-events-auto relative overflow-hidden max-w-lg w-full border-2"
              style={{
                background: 'rgba(10, 0, 30, 0.9)',
                borderColor: '#00FFFF',
                boxShadow: '0 0 40px rgba(0, 255, 255, 0.5)'
              }}
              data-testid="qr-scanner-modal"
            >
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
                data-testid="close-qr-scanner"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Camera className="w-8 h-8 text-cyan-400" style={{ filter: 'drop-shadow(0 0 8px #00FFFF)' }} />
                  <h2 className="text-2xl font-bold text-white" style={{ textShadow: '0 0 10px rgba(255, 255, 255, 0.5)' }}>
                    QR Code Client
                  </h2>
                </div>

                <p className="text-gray-300 mb-6">
                  Scannez le QR code du client ou tapez le code manuellement
                </p>

                {/* Manual Input - PRIORITAIRE */}
                <form onSubmit={handleManualSubmit} className="mb-6">
                  <label className="text-white text-sm font-semibold mb-2 block text-left">
                    Entrer le code manuellement
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                      placeholder="Ex: LPE-A1B2C3D4"
                      className="flex-1 px-4 py-3 rounded-xl border-2 border-cyan-500 bg-black/50 text-white text-center font-mono text-lg focus:outline-none focus:ring-4 focus:ring-cyan-500/50"
                      data-testid="manual-qr-input"
                      autoComplete="off"
                    />
                    <Button
                      type="submit"
                      disabled={!manualCode.trim()}
                      style={{
                        background: manualCode.trim() ? 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)' : 'rgba(100, 100, 100, 0.3)',
                        boxShadow: manualCode.trim() ? '0 0 30px rgba(0, 255, 255, 0.5)' : 'none'
                      }}
                      data-testid="submit-manual-qr"
                    >
                      OK
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-left">
                    Le client voit son code dans son espace personnel (/client)
                  </p>
                </form>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-gray-600"></div>
                  <span className="text-gray-500 text-sm">OU</span>
                  <div className="flex-1 h-px bg-gray-600"></div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/50 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-red-400 text-sm">{error}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        💡 Astuce: Si le scan ne fonctionne pas, demandez au client son code et tapez-le ci-dessus.
                      </p>
                    </div>
                  </div>
                )}

                {/* Scanner Toggle */}
                {!showScanner && (
                  <Button
                    onClick={startScanner}
                    variant="outline"
                    className="w-full mb-4"
                    style={{
                      borderColor: '#00FFFF',
                      color: '#00FFFF'
                    }}
                    data-testid="show-camera-button"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Activer la caméra
                  </Button>
                )}

                {/* QR Scanner */}
                {showScanner && (
                  <div className="mb-4">
                    <div 
                      id="qr-reader" 
                      ref={scannerRef}
                      className="w-full rounded-2xl overflow-hidden border-4 border-cyan-500"
                      style={{ minHeight: '250px' }}
                    />
                    
                    {/* Contrôles caméra */}
                    <div className="flex gap-2 mt-3 justify-center">
                      {cameraList.length > 1 && (
                        <Button
                          onClick={switchCamera}
                          variant="outline"
                          size="sm"
                          className="text-cyan-400 border-cyan-500"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Changer caméra
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          stopScanner();
                          setShowScanner(false);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-yellow-400 border-yellow-500"
                      >
                        Arrêter
                      </Button>
                    </div>
                    
                    {/* Info caméra */}
                    {selectedCamera && (
                      <p className="text-xs text-gray-500 mt-2">
                        <Smartphone className="w-3 h-3 inline mr-1" />
                        {selectedCamera.label || 'Caméra'} 
                        {cameraList.length > 1 && ` (${cameraList.indexOf(selectedCamera) + 1}/${cameraList.length})`}
                      </p>
                    )}
                  </div>
                )}

                {/* Tips pour Samsung A11 et appareils bas de gamme */}
                <div className="text-xs text-gray-500 mt-4 p-3 rounded-lg bg-gray-800/50">
                  <p className="font-semibold text-gray-400 mb-1">💡 Si le scan est difficile :</p>
                  <ul className="text-left space-y-1">
                    <li>• Assurez-vous d'être bien éclairé</li>
                    <li>• Tenez le QR code à ~15cm de la caméra</li>
                    <li>• Évitez les reflets sur l'écran du client</li>
                    <li>• En dernier recours, tapez le code manuellement</li>
                  </ul>
                </div>

                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="w-full mt-4"
                  style={{
                    background: 'rgba(100, 100, 100, 0.3)',
                    borderColor: '#666666',
                    color: '#FFFFFF'
                  }}
                  data-testid="cancel-qr-scan"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
